// A school join code waiting for the parent to finish signing in (P-JOIN-5; 002 FR-036, FR-037;
// 003 FR-012 to FR-016; data-model §3). Kept for 24 hours with the join source (LINK or QR);
// expired or malformed values read as null and are removed. A stored 002 value (v1) reads as LINK.
// When the device refuses to store it (private mode, blocked storage), the value lives in memory
// for this tab only, so the join still works until the app is closed.
import { pendingJoinStorage } from "./pendingJoinStorage";

export type PendingJoinSource = "LINK" | "QR";
export type PendingJoin = {
  v: 2;
  code: string;
  source: PendingJoinSource;
  expiresAt: string;
  /** The school's name from the landing, so sign-up and log-in can say who is being joined. */
  schoolName?: string;
};

const KEY = "akaiva.pendingJoin";
const LIFETIME_MS = 24 * 60 * 60 * 1000;
/** P-JOIN-1 alphabet without look-alikes (no I, O, 0, 1). */
export const JOIN_CODE = /^[A-HJ-NP-Z2-9]{6}$/;

let memory: string | null = null;

function parse(raw: string | null): PendingJoin | null {
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const v = value as Record<string, unknown>;
    if (
      (v.v !== 1 && v.v !== 2) ||
      typeof v.code !== "string" ||
      !JOIN_CODE.test(v.code) ||
      typeof v.expiresAt !== "string" ||
      Number.isNaN(Date.parse(v.expiresAt))
    )
      return null;
    if (v.v === 2 && v.source !== "LINK" && v.source !== "QR") return null;
    return {
      v: 2,
      code: v.code,
      source: v.v === 2 ? (v.source as PendingJoinSource) : "LINK",
      expiresAt: v.expiresAt,
      ...(typeof v.schoolName === "string" && v.schoolName.length <= 120
        ? { schoolName: v.schoolName }
        : {}),
    };
  } catch {
    return null;
  }
}

async function remove(): Promise<void> {
  memory = null;
  await pendingJoinStorage.remove(KEY);
}

export const pendingJoin = {
  async read(now: number = Date.now()): Promise<PendingJoin | null> {
    const raw = (await pendingJoinStorage.get(KEY)) ?? memory;
    const value = parse(raw);
    if (
      raw !== null &&
      (value === null || Date.parse(value.expiresAt) <= now)
    ) {
      await remove();
      return null;
    }
    return value;
  },
  async save(
    code: string,
    now: number = Date.now(),
    source: PendingJoinSource = "LINK",
    schoolName?: string,
  ): Promise<void> {
    if (!JOIN_CODE.test(code)) return;
    const value: PendingJoin = {
      v: 2,
      code,
      source,
      expiresAt: new Date(now + LIFETIME_MS).toISOString(),
      ...(schoolName ? { schoolName } : {}),
    };
    const raw = JSON.stringify(value);
    const stored = await pendingJoinStorage.set(KEY, raw);
    memory = stored ? null : raw;
  },
  /** Removes the pending join only when it holds `code` (FR-008, FR-020). */
  async clearIfCode(code: string): Promise<void> {
    const raw = (await pendingJoinStorage.get(KEY)) ?? memory;
    if (parse(raw)?.code === code) await remove();
  },
  async clear(): Promise<void> {
    await remove();
  },
};
